import { useState, useEffect, useCallback, useRef } from 'react';
import useHankoAuth from './useHankoAuth';
import { IDENTITY_BUILD_PENDING_KEY } from '../components/onboarding/hooks/useOnboardingSubmit';
import { isIdentityReviewed, markIdentityReviewed, subscribeIdentityReviewed } from '../utils/identityNudge';

const POLL_INTERVAL_MS = 10 * 1000;
// Stop polling after this long — orgs whose enrichment never produces a usable
// fingerprint (e.g. VAT lookup failed entirely) would otherwise poll forever.
const MAX_POLL_DURATION_MS = 10 * 60 * 1000;

// DEV-only manual override so every nudge/studio state can be exercised without
// waiting for a real enrichment run:
//   localStorage.setItem('__identityStatusOverride', JSON.stringify({
//     ready: true, reviewStatus: 'PENDING', verifiedNoticeCount: 268,
//     keywords: ['tekoäly'], cpvCodes: [{ code: '72000000', description: '...' }],
//     nutsCodes: [{ code: 'FI1B', description: '...' }]
//   }))
// Remove the key to return to live polling. Ignored in production builds.
const readDevOverride = () => {
    if (!import.meta.env.DEV) return null;
    try {
        return JSON.parse(window.localStorage.getItem('__identityStatusOverride'));
    } catch {
        return null;
    }
};

/**
 * Polls the proactive-onboarding-nudge readiness status (java-microservice-bff
 * OrganizationIdentityStatusController) so IdentityReadyFab knows when to appear -- and,
 * while it's still building, lets it show a lightweight "building your profile" indicator
 * instead of leaving the user with zero feedback that anything is happening.
 *
 * Polling is gated behind IDENTITY_BUILD_PENDING_KEY (set once by useOnboardingSubmit right
 * after a fresh signup), not just `isOnboarded` -- otherwise every already-onboarded account,
 * which will never have an identity, would poll for up to 10 minutes on every single page
 * load for nothing. The flag is cleared once the outcome is fully known (REVIEWED) or the
 * poll window times out -- reviewStatus PENDING/OPENED both keep polling, since OPENED means
 * the nudge is still open/unfinished, not done.
 */
const useOrganizationIdentityStatus = () => {
    const { isOnboarded, callApi } = useHankoAuth();
    const [status, setStatus] = useState(readDevOverride);
    const [polling, setPolling] = useState(false);
    const [timedOut, setTimedOut] = useState(false);
    // Selaimen laajuinen lippu: profiili tallennettu jostain pinnasta (ks. utils/identityNudge).
    const [locallyReviewed, setLocallyReviewed] = useState(isIdentityReviewed);
    const startedAtRef = useRef(null);

    useEffect(() => subscribeIdentityReviewed(() => setLocallyReviewed(isIdentityReviewed())), []);

    const fetchStatus = useCallback(async () => {
        const override = readDevOverride();
        if (override) {
            setStatus(override);
            return override;
        }
        try {
            const data = await callApi('GET', '/api/organization-identity/status');
            setStatus(data);
            return data;
        } catch (error) {
            return null;
        }
    }, [callApi]);

    useEffect(() => {
        if (!isOnboarded || localStorage.getItem(IDENTITY_BUILD_PENDING_KEY) !== 'true') {
            return undefined;
        }
        if (locallyReviewed) {
            // Profiili on jo tallennettu: pollaus olisi turhaa, ja lippu siivotaan pois.
            localStorage.removeItem(IDENTITY_BUILD_PENDING_KEY);
            return undefined;
        }

        startedAtRef.current = Date.now();
        let cancelled = false;
        const timeoutRef = { current: null };
        setPolling(true);

        const tick = async () => {
            if (cancelled) return;
            const data = await fetchStatus();
            // Only a real save (REVIEWED) or a confirmed dead end (verified with zero matching
            // notices -- nothing worth ever nudging with) fully stops polling. PENDING and OPENED
            // both keep going: OPENED means the nudge is out there unfinished, so we keep checking
            // in case the user saves a profile from the chat (a different, unrelated component).
            const done = data?.reviewStatus === 'REVIEWED'
                || (data?.ready && (data?.verifiedNoticeCount ?? 0) === 0);
            const expired = Date.now() - startedAtRef.current > MAX_POLL_DURATION_MS;
            if (!cancelled && !done && !expired) {
                timeoutRef.current = setTimeout(tick, POLL_INTERVAL_MS);
            } else if (!cancelled) {
                localStorage.removeItem(IDENTITY_BUILD_PENDING_KEY);
                setPolling(false);
                if (expired && !done) setTimedOut(true);
            }
        };

        tick();

        return () => {
            cancelled = true;
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
        };
    }, [isOnboarded, fetchStatus, locallyReviewed]);

    // Optimistic local update right after seeding, so the FAB switches to the "continue" state
    // immediately rather than waiting for the next poll tick.
    const markOpenedLocally = useCallback((conversationId) => {
        setStatus((prev) => (prev ? { ...prev, reviewStatus: 'OPENED', conversationId } : prev));
    }, []);

    // The first profile was just saved (from the Profile Studio): hide every nudge
    // surface immediately, without waiting for the next poll tick. Server-side
    // persistence of REVIEWED is the backend's responsibility on profile save.
    const markReviewedLocally = useCallback(() => {
        setStatus((prev) => (prev ? { ...prev, reviewStatus: 'REVIEWED' } : prev));
        markIdentityReviewed();
    }, []);

    const reviewed = locallyReviewed || status?.reviewStatus === 'REVIEWED';

    const isNudgeReady = !!status?.ready
        && !reviewed
        && (status?.verifiedNoticeCount ?? 0) > 0;

    // Still polling, and we don't yet know the outcome either way.
    const isBuilding = polling && !isNudgeReady && !reviewed;

    // Terminal no-suggestion state: verified with zero matching notices, or the
    // poll window expired without a usable fingerprint. Surfaced as the assisted-
    // creation fallback card instead of silently disappearing.
    const hasFailed = !reviewed
        && ((!!status?.ready && (status?.verifiedNoticeCount ?? 0) === 0) || timedOut);

    return { status, isNudgeReady, isBuilding, hasFailed, markOpenedLocally, markReviewedLocally };
};

export default useOrganizationIdentityStatus;
